/**
 * Bọc hàm `mount` của module viết bằng framework bất kỳ thành một component React (ADR-0110), để phần còn lại
 * của cổng (`VungRemote`, ranh giới lỗi, màn xem trước) coi nó như mọi remote React. Cổng là bên giữ vòng đời:
 * gọi `mount` khi hiện màn, `update` khi props đổi, `unmount` khi rời màn hoặc nâng cấp module.
 */
import { createElement, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';

import type { ModuleAppProps } from './nap-remote';

/** Đồng bộ với `MountHandle`, `MountFn` của `sdk/module-web/src/mount.ts`. */
export interface MountHandle {
  update?(props: ModuleAppProps): void;
  unmount?(): void;
}
export type MountFn = (el: HTMLElement, props: ModuleAppProps) => MountHandle | void | Promise<MountHandle | void>;

export function taoManHinhTuMount(mount: MountFn): ComponentType<ModuleAppProps> {
  function ManHinhMount(props: ModuleAppProps) {
    const khung = useRef<HTMLDivElement>(null);
    const tayCam = useRef<MountHandle | null>(null);
    const propsMoiNhat = useRef(props);
    propsMoiNhat.current = props;
    const [loi, datLoi] = useState<unknown>(null);
    // Ném khi vẽ để `RanhGioiLoiModule` bắt, như lỗi của remote React
    if (loi) throw loi;

    // Gắn một ứng dụng ngoài vào DOM của khung: việc duy nhất cần effect, không có cách nào khác
    useEffect(() => {
      const khungNgoai = khung.current;
      if (!khungNgoai) return;
      // Mỗi lần mount một phần tử riêng: React StrictMode mount hai lần liền, mà nhiều framework (Vue…) gắn trạng thái
      // vào phần tử gốc, nên hai ứng dụng dùng chung một phần tử sẽ gỡ nhầm nhau khi lần đầu được unmount về sau.
      const el = document.createElement('div');
      khungNgoai.append(el);
      let daGo = false;
      const luc = propsMoiNhat.current;
      const nhanTayCam = (h: MountHandle | void) => {
        if (daGo) {
          h?.unmount?.();
          return;
        }
        tayCam.current = h ?? null;
        // Props đổi trong lúc `mount` bất đồng bộ còn chạy: báo lại để module không giữ bản cũ
        if (propsMoiNhat.current !== luc) h?.update?.(propsMoiNhat.current);
      };
      try {
        const kq = mount(el, luc);
        if (kq && typeof (kq as Promise<unknown>).then === 'function') {
          (kq as Promise<MountHandle | void>).then(nhanTayCam, (e: unknown) => {
            if (!daGo) datLoi(e);
          });
        } else {
          nhanTayCam(kq as MountHandle | void);
        }
      } catch (e) {
        datLoi(e);
      }
      return () => {
        daGo = true;
        const h = tayCam.current;
        tayCam.current = null;
        h?.unmount?.();
        el.remove();
      };
    }, []);

    useEffect(() => {
      tayCam.current?.update?.(props);
    }, [props.basePath, props.path, props.locale, props.navigate, props.hasPermission, props.getAccessToken]);

    return createElement('div', { ref: khung, 'data-module-mount': '' });
  }
  ManHinhMount.displayName = 'ManHinhMount';
  return ManHinhMount;
}
